import { useCallback, useMemo, useState } from 'react';
import { useDebouncedValue } from '@mantine/hooks';
import { productsService } from '../../../../services/products/products.service';
import type { PosLimitItemsSectionProps, ResetConfirmState, StatFilter } from './types';

const SEARCH_DEBOUNCE_MS = 200;

export const usePosLimitItemsController = ({
  posProducts,
  employeeSession,
  isManager,
  onLimitSaveError,
}: Pick<PosLimitItemsSectionProps, 'posProducts' | 'employeeSession' | 'isManager' | 'onLimitSaveError'>) => {
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);
  const [statFilter, setStatFilter] = useState<StatFilter>('all');
  const [selectedProductIds, setSelectedProductIds] = useState<Set<number>>(() => new Set());
  const [numPadProductId, setNumPadProductId] = useState<number | null>(null);
  const [bulkSelection, setBulkSelection] = useState<number[] | null>(null);
  const [isSavingLimit, setIsSavingLimit] = useState(false);
  const [limitError, setLimitError] = useState<string | null>(null);
  const [resetConfirm, setResetConfirm] = useState<ResetConfirmState | null>(null);

  const visibleBaseProducts = useMemo(
    () => (isManager ? posProducts : posProducts.filter((product) => product.limit !== null)),
    [isManager, posProducts],
  );

  const filteredProducts = useMemo(() => {
    const needle = debouncedSearch.trim().toLowerCase();
    return visibleBaseProducts.filter((product) => {
      if (statFilter === 'limited' && !(product.limit !== null && product.limit > 0)) {
        return false;
      }
      if (statFilter === 'out' && product.limit !== 0) {
        return false;
      }
      if (needle && !product.name.toLowerCase().includes(needle)) {
        return false;
      }
      return true;
    });
  }, [visibleBaseProducts, debouncedSearch, statFilter]);

  const visibleSelectedProductIds = useMemo(
    () => filteredProducts.filter((product) => selectedProductIds.has(product.id)).map((product) => product.id),
    [filteredProducts, selectedProductIds],
  );

  const visibleClearableProductIds = useMemo(
    () => filteredProducts.filter((product) => product.limit !== null).map((product) => product.id),
    [filteredProducts],
  );

  const visibleSelectedClearableProductIds = useMemo(
    () =>
      filteredProducts
        .filter((product) => selectedProductIds.has(product.id) && product.limit !== null)
        .map((product) => product.id),
    [filteredProducts, selectedProductIds],
  );

  const totalCount = visibleBaseProducts.length;
  const outOfStockCount = useMemo(
    () => visibleBaseProducts.filter((product) => product.limit === 0).length,
    [visibleBaseProducts],
  );
  const limitedCount = useMemo(
    () => visibleBaseProducts.filter((product) => product.limit !== null && product.limit > 0).length,
    [visibleBaseProducts],
  );

  const persistLimits = useCallback(
    async (productIds: number[], value: number | null): Promise<boolean> => {
      if (!employeeSession || !isManager) {
        const message = 'רק מנהל יכול לעדכן הגבלת מוצר';
        setLimitError(message);
        onLimitSaveError?.(message);
        return false;
      }
      if (productIds.length === 0) {
        return false;
      }
      setIsSavingLimit(true);
      setLimitError(null);
      try {
        await productsService.updateLimits(productIds, value, employeeSession);
        return true;
      } catch (saveError: unknown) {
        const message = saveError instanceof Error ? saveError.message : 'Failed to save limit';
        setLimitError(message);
        onLimitSaveError?.(message);
        return false;
      } finally {
        setIsSavingLimit(false);
      }
    },
    [employeeSession, isManager, onLimitSaveError],
  );

  const handleClearLimit = useCallback(
    (productId: number) => {
      void persistLimits([productId], null);
    },
    [persistLimits],
  );

  const executeClearLimits = (confirmedReset: ResetConfirmState) => {
    const targetIdSet = new Set(confirmedReset.productIds);
    const scope = posProducts.filter((product) => targetIdSet.has(product.id));
    const targetIds = scope.filter((product) => product.limit !== null).map((product) => product.id);
    void persistLimits(targetIds, null).then((ok) => {
      if (ok && confirmedReset.kind === 'selected') {
        setSelectedProductIds((previous) => {
          const next = new Set(previous);
          confirmedReset.productIds.forEach((id) => next.delete(id));
          return next;
        });
      }
    });
  };

  const handleClearLimits = () => {
    if (selectedProductIds.size > 0) {
      if (visibleSelectedClearableProductIds.length === 0) {
        return;
      }
      setResetConfirm({ kind: 'selected', productIds: visibleSelectedClearableProductIds });
      return;
    }
    if (visibleClearableProductIds.length === 0) {
      return;
    }
    setResetConfirm({ kind: 'all', productIds: visibleClearableProductIds });
  };

  const handleConfirmReset = () => {
    if (!resetConfirm) {
      return;
    }
    const confirmedReset = resetConfirm;
    setResetConfirm(null);
    executeClearLimits(confirmedReset);
  };

  const hasSameLimitForAll = (productIds: number[], value: number | null): boolean => {
    if (productIds.length === 0) {
      return false;
    }
    return productIds.every((id) => (posProducts.find((product) => product.id === id)?.limit ?? null) === value);
  };

  const handleToggleSelected = useCallback((productId: number, checked: boolean) => {
    setSelectedProductIds((previous) => {
      const next = new Set(previous);
      if (checked) {
        next.add(productId);
      } else {
        next.delete(productId);
      }
      return next;
    });
  }, []);

  const handleToggleSelectAllFiltered = () => {
    setSelectedProductIds((previous) => {
      const next = new Set(previous);
      const allSelected = filteredProducts.every((product) => next.has(product.id));
      filteredProducts.forEach((product) => {
        if (allSelected) {
          next.delete(product.id);
        } else {
          next.add(product.id);
        }
      });
      return next;
    });
  };

  const handleOpenNumPad = useCallback((productId: number) => {
    setLimitError(null);
    setBulkSelection(null);
    setNumPadProductId(productId);
  }, []);

  const handleOpenBulkNumPad = () => {
    if (visibleSelectedProductIds.length === 0) {
      return;
    }
    setLimitError(null);
    setNumPadProductId(null);
    setBulkSelection(visibleSelectedProductIds);
  };

  const handleClearSelection = () => {
    setSelectedProductIds((previous) => {
      const next = new Set(previous);
      visibleSelectedProductIds.forEach((id) => next.delete(id));
      return next;
    });
  };

  const handleCloseNumPad = () => {
    setNumPadProductId(null);
    setBulkSelection(null);
  };

  const handleConfirmNumPad = async (value: number | null) => {
    if (bulkSelection !== null) {
      if (hasSameLimitForAll(bulkSelection, value)) {
        setBulkSelection(null);
        return;
      }
      const ok = await persistLimits(bulkSelection, value);
      if (ok) {
        setBulkSelection(null);
        setSelectedProductIds(new Set());
      }
      return;
    }
    if (numPadProductId !== null) {
      const currentLimit = posProducts.find((product) => product.id === numPadProductId)?.limit ?? null;
      if (currentLimit === value) {
        setNumPadProductId(null);
        return;
      }
      const ok = await persistLimits([numPadProductId], value);
      if (ok) {
        setNumPadProductId(null);
      }
    }
  };

  const editingProduct = useMemo(
    () => (numPadProductId === null ? null : posProducts.find((p) => p.id === numPadProductId) ?? null),
    [numPadProductId, posProducts],
  );

  const selectedCount = visibleSelectedProductIds.length;
  const hasSelection = selectedCount > 0;
  const selectedClearableCount = visibleSelectedClearableProductIds.length;
  const hasGlobalSelection = selectedProductIds.size > 0;
  const hasClearAction = hasGlobalSelection
    ? selectedClearableCount > 0
    : visibleClearableProductIds.length > 0;
  const clearButtonLabel = hasSelection ? `אפס (${selectedClearableCount})` : 'אפס הכל';

  const allFilteredSelected =
    filteredProducts.length > 0 && filteredProducts.every((product) => selectedProductIds.has(product.id));
  const someFilteredSelected =
    !allFilteredSelected && filteredProducts.some((product) => selectedProductIds.has(product.id));
  const hasSelectionActions = isManager && hasSelection;

  const isBulkMode = bulkSelection !== null;
  const numPadOpen = editingProduct !== null || isBulkMode;
  const numPadTitle = isBulkMode ? 'הגדר מגבלה לפריטים שנבחרו' : 'הגדר מגבלת פריט';
  const numPadSubject = isBulkMode
    ? `${bulkSelection?.length ?? 0} פריטים נבחרו`
    : (editingProduct?.name ?? '');
  const numPadInitial = isBulkMode ? null : (editingProduct?.limit ?? null);
  const resetConfirmMessage =
    resetConfirm?.kind === 'selected'
      ? `האם אתה בטוח שברצונך לאפס הגבלות עבור ${resetConfirm.productIds.length} פריטים?`
      : 'האם אתה בטוח שברצונך לאפס את כל ההגבלות';

  return {
    search,
    statFilter,
    selectedProductIds,
    isSavingLimit,
    limitError,
    resetConfirm,
    filteredProducts,
    totalCount,
    outOfStockCount,
    limitedCount,
    hasClearAction,
    clearButtonLabel,
    allFilteredSelected,
    someFilteredSelected,
    hasSelectionActions,
    numPadOpen,
    numPadTitle,
    numPadSubject,
    numPadInitial,
    resetConfirmMessage,
    setSearch,
    setStatFilter,
    setResetConfirm,
    handleToggleSelectAllFiltered,
    handleOpenBulkNumPad,
    handleClearSelection,
    handleClearLimits,
    handleToggleSelected,
    handleOpenNumPad,
    handleClearLimit,
    handleCloseNumPad,
    handleConfirmNumPad,
    handleConfirmReset,
  };
};
